import { JSONSchema7Object } from 'json-schema';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import {
  ExternalIntegrationLog,
  ExternalIntegrationLogSchema,
} from 'dashboard/models/external-integration-log';
import { ExternalIntegrationVendor } from 'dashboard/models/external-integration-vendor';
import { dashboardApi } from 'dashboard/redux/api/api';
import { ApiEndpoint, ApiModel, ApiResponse } from 'lib/interfaces/api';

const api = dashboardApi.injectEndpoints({
  endpoints: (builder) => ({
    getExternalIntegrationVendor: builder.query<ExternalIntegrationVendor, string>({
      query: async (vendor) =>
        await SessionClientDashboard.getResourceMeta<ExternalIntegrationVendor>(
          ApiEndpoint.ExternalIntegrationVendor,
          vendor,
        ),
      transformResponse: (response: ApiResponse<never, ExternalIntegrationVendor>) => {
        if (response.meta) {
          return response.meta;
        } else {
          throw new Error('External integration vendor not loaded');
        }
      },
      providesTags: (result) =>
        result ? [{ type: ApiModel.ExternalIntegrationVendor, id: result.vendor }] : [],
      keepUnusedDataFor: 3600,
    }),
    listExternalIntegrationVendors: builder.query<ExternalIntegrationVendor[], void>({
      query: async () =>
        await SessionClientDashboard.listResource<never, { vendors: ExternalIntegrationVendor[] }>(
          ApiEndpoint.ExternalIntegrationVendor,
        ),
      transformResponse: (response: ApiResponse<never, { vendors: ExternalIntegrationVendor[] }>) =>
        response.meta?.vendors || [],
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ vendor }) => ({
                type: ApiModel.ExternalIntegrationVendor as const,
                id: vendor,
              })),
              { type: ApiModel.ExternalIntegrationVendor, id: 'LIST' },
            ]
          : [{ type: ApiModel.ExternalIntegrationVendor, id: 'LIST' }],
    }),
    testExternalIntegrationVendor: builder.mutation<
      ApiResponse<ExternalIntegrationLog, ExternalIntegrationLogSchema>,
      {
        vendorToken: string;
        requestData: JSONSchema7Object | null;
        requestParameters: JSONSchema7Object;
        operationId: string;
        integrationCredential: string;
      }
    >({
      query: async ({
        vendorToken,
        requestData,
        requestParameters,
        operationId,
        integrationCredential,
      }) => {
        return await SessionClientDashboard.postResourceAction(
          {
            type: ApiModel.ExternalIntegrationVendor,
            id: vendorToken,
            operation_id: operationId,
            request_body: requestData,
            request_parameters: requestParameters,
            integration_credential: integrationCredential,
          },
          'test-operation',
        );
      },
      invalidatesTags: () => [{ type: ApiModel.ExternalIntegrationLog, id: 'LIST' }],
    }),
  }),
});

export const {
  useGetExternalIntegrationVendorQuery,
  useListExternalIntegrationVendorsQuery,
  useTestExternalIntegrationVendorMutation,
} = api;
